import { useState, useEffect } from "react";
import { useAuth, apiClient } from "../App";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { 
  Palette, ArrowLeft, Download, Printer, RefreshCw, 
  CreditCard, Loader2, CheckCircle, ZoomIn, Share2 
} from "lucide-react";
import ShareButtons from "../components/ShareButtons";
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";

const Preview = () => {
  const { user, refreshUser } = useAuth();
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regeneratingPage, setRegeneratingPage] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    if (!projectId) {
      toast.error("Invalid project link");
      navigate("/dashboard");
      return;
    }
    
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error("Error fetching project:", error);
      if (error.response?.status === 404) {
        toast.error("Project not found");
      } else if (error.response?.status === 401) {
        toast.error("Please log in to view this project");
        navigate("/login");
        return;
      } else {
        toast.error("Failed to load project");
      }
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleRegeneratePage = async (pageIndex) => {
    if ((user?.credits || 0) < 1) {
      toast.error("You need at least 1 credit to regenerate a page");
      navigate("/pricing");
      return;
    }

    setRegeneratingPage(pageIndex);
    
    try {
      await apiClient.post(
        `/projects/${projectId}/regenerate-page`,
        { project_id: projectId, page_index: pageIndex },
        { timeout: 120000 }
      );
      
      await fetchProject();
      await refreshUser();
      toast.success("Page regenerated successfully!");
    } catch (error) {
      console.error("Regenerate error:", error);
      if (error.response?.status === 402) {
        toast.error("Insufficient credits");
        navigate("/pricing");
      } else {
        toast.error("Failed to regenerate page");
      }
    } finally {
      setRegeneratingPage(null);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    
    try {
      const response = await apiClient.get(`/projects/${projectId}/pdf`, {
        responseType: "blob",
        timeout: 60000, // 60 second timeout
      });
      
      // Create blob with correct PDF type
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement("a");
      link.href = url;
      const filename = `colormagic_${project?.theme?.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20)}.pdf`;
      link.setAttribute("download", filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      if (error.response?.status === 400) {
        toast.error("Project is not yet completed. Please wait for generation to finish.");
      } else if (error.response?.status === 404) {
        toast.error("Project not found.");
      } else {
        toast.error("Failed to download PDF. Please try again.");
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrint = () => {
    // Open a new window with just the images for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Please allow popups to print");
      return;
    }
    
    const imagesHtml = project.pages.map((page, index) => `
      <div style="page-break-after: always; display: flex; justify-content: center; align-items: center; height: 100vh; padding: 20px; box-sizing: border-box;">
        <img src="data:image/png;base64,${page}" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
      </div>
    `).join('');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ColorWonder - ${project.theme}</title>
          <style>
            @media print {
              body { margin: 0; padding: 0; }
              @page { margin: 0.5cm; size: A4; }
            }
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          </style>
        </head>
        <body>
          ${imagesHtml}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient">
        <div className="loader"></div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 no-print">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </Link>

            <div className="flex items-center gap-3">
              <div className="credit-badge" data-testid="preview-credits">
                <CreditCard className="w-4 h-4" />
                <span>{user?.credits || 0} Credits</span>
              </div>
              
              {/* Share Button */}
              {project && (
                <ShareButtons 
                  projectId={projectId} 
                  theme={project.theme} 
                  variant="icon" 
                />
              )}
              
              <Button
                variant="outline"
                onClick={handlePrint}
                className="rounded-full"
                data-testid="print-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              
              <Button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf || project.status !== "completed"}
                className="rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white btn-lift"
                data-testid="download-pdf-btn"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Project Info */}
        <div className="mb-8 no-print">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
              <Palette className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                {project.theme}
              </h1>
              <p className="text-slate-500 text-sm">
                {project.age_group} • {project.style} • {project.pages?.length || 0} pages
              </p>
            </div>
          </div>
          
          {project.watermarked && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
              <span className="text-amber-700 text-sm">
                Free plan pages include a watermark. <Link to="/pricing" className="font-semibold underline">Upgrade</Link> for watermark-free, high-resolution downloads.
              </span>
            </div>
          )}
        </div>

        {/* Pages Grid */}
        {project.pages?.length > 0 ? (
          <div className="coloring-page-grid">
            {project.pages.map((page, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-4 border border-slate-100 card-hover"
              >
                <div className="relative group">
                  <img
                    src={`data:image/png;base64,${page}`}
                    alt={`Coloring page ${index + 1}`}
                    className="preview-image w-full"
                    data-testid={`page-image-${index}`}
                  />
                  
                  {/* Overlay buttons */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-xl flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setSelectedImage(page)}
                      className="rounded-full bg-white/90 hover:bg-white"
                      data-testid={`zoom-page-${index}`}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="mt-3 flex items-center justify-between no-print">
                  <span className="text-sm font-medium text-slate-600">
                    Page {index + 1}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRegeneratePage(index)}
                    disabled={regeneratingPage !== null}
                    className="text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                    data-testid={`regenerate-page-${index}`}
                  >
                    {regeneratingPage === index ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Regenerate (1 credit)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
            <Loader2 className="w-12 h-12 text-violet-300 animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Generating your pages...
            </h3>
            <p className="text-slate-500">
              This may take a few minutes. Please don't close this page.
            </p>
          </div>
        )}

        {/* Success Message */}
        {project.status === "completed" && project.pages?.length > 0 && (
          <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-2xl no-print">
            <div className="flex items-center gap-4 mb-4">
              <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-green-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  Your coloring book is ready!
                </h3>
                <p className="text-green-700 text-sm">
                  Download the PDF or print individual pages. Each page is optimized for A4 paper.
                </p>
              </div>
            </div>
            
            {/* Share Section */}
            <div className="pt-4 border-t border-green-200">
              <p className="text-sm text-green-700 mb-3">Love your creation? Share it with friends and family!</p>
              <ShareButtons projectId={projectId} theme={project.theme} variant="buttons" />
            </div>
          </div>
        )}
      </main>

      {/* Image Zoom Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {selectedImage && (
            <img
              src={`data:image/png;base64,${selectedImage}`}
              alt="Zoomed coloring page"
              className="w-full h-auto"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Preview;
